/* eslint-disable sonarjs/no-identical-functions */
import { defineStore, storeToRefs } from 'pinia';
import { computed } from '@vue/composition-api';
import type { ComputedRef } from '@vue/composition-api';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { EnumAppMode } from '@getaccept/lib-shared/src/enums/AppMode';
import { UserRoleLegacy } from '@getaccept/lib-shared/src/users/types/user-role-legacy';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';

import { ModuleName } from '@getaccept/lib-shared/src/entity/app-modules/enums/app-module';
import { useSessionStore } from '../../session/store/session.store';
import { SidebarLabel } from '../types/enums/sidebar-label';
import type { GlobalSidebarItem } from '../types/global-sidebar-item';
import { useModulesStore } from '../../modules/store/modules.store';
import { useSubscriptionStore } from '../../subscription/store/subscription.store';

export const useSidebarItemsStore = defineStore('sidebar-items', () => {
  const sessionStore = useSessionStore();
  const { features } = storeToRefs(useFeatureStore());
  const { isInactiveSubscription } = storeToRefs(useSubscriptionStore());
  const modulesStore = useModulesStore();

  const appMode = computed(() => sessionStore.session?.app_mode);
  const userRole = computed(() => sessionStore.session?.user_role);
  const showDealboard = computed(
    () => !features.value[Feature.Dealboard].restricted && appMode.value === EnumAppMode.Sales
  );
  const showReports = computed(
    () => !features.value[Feature.Reports].restricted && appMode.value === EnumAppMode.Sales
  );
  const showProductLibrary = computed(
    () =>
      !features.value[Feature.ProductLibrary].restricted && userRole.value === UserRoleLegacy.Admin
  );

  const allContractsModuleSidebarItems: ComputedRef<GlobalSidebarItem[]> = computed(() => [
    {
      label: SidebarLabel.Dashboard,
      pathName: PathName.Dashboard,
      show: !isInactiveSubscription.value,
    },
    {
      label: SidebarLabel.Dealboard,
      pathName: PathName.Dealboard,
      show: showDealboard.value && !isInactiveSubscription.value,
    },
    {
      label: SidebarLabel.Documents,
      pathName: PathName.Documents,
      show: !isInactiveSubscription.value,
    },
    {
      label: SidebarLabel.Contracts,
      pathName: PathName.Contracts,
      show:
        (!features.value[Feature.ContractManagement].restricted && !isInactiveSubscription.value) ||
        (!features.value[Feature.ContractManagement].restricted &&
          userRole.value === UserRoleLegacy.Admin &&
          isInactiveSubscription.value),
    },
    {
      label: SidebarLabel.Products,
      pathName: PathName.ProductLibrary,
      show: showProductLibrary.value && !isInactiveSubscription.value,
    },
    {
      label: SidebarLabel.Content,
      pathName: PathName.ContentLibrary,
      show: !isInactiveSubscription.value,
    },
    {
      label: SidebarLabel.Archive,
      pathName: PathName.Archive,
      show:
        (!features.value[Feature.Archive].restricted && !isInactiveSubscription.value) ||
        (!features.value[Feature.Archive].restricted &&
          userRole.value === UserRoleLegacy.Admin &&
          isInactiveSubscription.value),
    },
    {
      label: SidebarLabel.Reports,
      pathName: PathName.Reports,
      show: showReports.value && !isInactiveSubscription.value,
    },
    {
      label: SidebarLabel.Contacts,
      pathName: PathName.Contacts,
      show: !isInactiveSubscription.value,
    },
  ]);

  const contractsModuleSidebarItems: ComputedRef<GlobalSidebarItem[]> = computed(() =>
    allContractsModuleSidebarItems.value.filter(({ show }) => show)
  );

  const allDealroomModuleSidebarItems: ComputedRef<GlobalSidebarItem[]> = computed(() => [
    {
      label: SidebarLabel.Rooms,
      pathName: PathName.Rooms,
      show: true,
    },
    {
      label: SidebarLabel.Content,
      pathName: PathName.Library,
      show: true,
    },
    {
      label: SidebarLabel.Products,
      pathName: PathName.ProductLibrary,
      show: showProductLibrary.value,
    },
    {
      label: SidebarLabel.Contacts,
      pathName: PathName.Contacts,
      show: true,
    },
  ]);

  const dealroomModuleSidebarItems: ComputedRef<GlobalSidebarItem[]> = computed(() =>
    allDealroomModuleSidebarItems.value.filter(({ show }) => show)
  );

  const sidebarItems = computed(() => {
    switch (modulesStore.activeModule) {
      case ModuleName.Contracts:
        return contractsModuleSidebarItems.value;
      case ModuleName.DealRooms:
        return dealroomModuleSidebarItems.value;
      default:
        return [];
    }
  });

  return {
    sidebarItems,
  };
});
